import api from './api';

export interface Policy {
  id: string;
  title: string;
  content: string;
  type: string;
  /** @deprecated Always present as legacy data; new flow uses storageKey/viewableKey under the hood. */
  documentUrl?: string;
  /** True when the policy has a viewable attachment (PDF or convertible Office doc). */
  hasDocument?: boolean;
  mimeType?: string | null;
  conversionStatus?: 'native' | 'pending' | 'ready' | 'failed';
  isActive: boolean;
  requireAcceptance: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PolicyStatus {
  unacceptedPolicies: Policy[];
  hasAcceptedAll: boolean;
}

export interface SecurePolicyUploadResult {
  storageKey: string;
  viewableKey: string | null;
  mimeType: string;
  conversionStatus: 'native' | 'pending' | 'ready' | 'failed';
}

export const policiesService = {
  async getAll(): Promise<Policy[]> {
    const response = await api.get('/policies');
    return response.data;
  },

  async getActive(): Promise<Policy[]> {
    const response = await api.get('/policies/active');
    return response.data;
  },

  async getUserStatus(): Promise<PolicyStatus> {
    const response = await api.get('/policies/user-status');
    return response.data;
  },

  async acceptPolicy(policyId: string): Promise<void> {
    await api.post(`/policies/accept/${policyId}`);
  },

  async create(data: {
    title: string;
    content: string;
    type?: string;
    storageKey?: string;
    viewableKey?: string | null;
    mimeType?: string;
    conversionStatus?: 'native' | 'pending' | 'ready' | 'failed';
    requireAcceptance?: boolean;
    isActive?: boolean;
  }): Promise<Policy> {
    const response = await api.post('/policies', data);
    return response.data;
  },

  async update(id: string, data: Partial<Policy> & { storageKey?: string; viewableKey?: string | null; mimeType?: string; conversionStatus?: 'native' | 'pending' | 'ready' | 'failed' }): Promise<Policy> {
    const response = await api.put(`/policies/${id}`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/policies/${id}`);
  },

  /**
   * Upload a policy attachment through the secure pipeline.
   * Returns storage tokens that the form passes to create/update. The original
   * file URL never reaches the client.
   */
  async uploadDocument(file: File): Promise<SecurePolicyUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/policies/upload', formData);
    return response.data;
  },

  /** Metadata for the SecureViewer header. */
  async getMeta(id: string): Promise<{
    id: string;
    title: string;
    mimeType: string | null;
    conversionStatus: 'native' | 'pending' | 'ready' | 'failed';
  }> {
    const response = await api.get(`/policies/${id}/meta`);
    return response.data;
  },

  /** Fetch the PDF bytes used by the SecureViewer. */
  async getViewBytes(id: string): Promise<ArrayBuffer> {
    const response = await api.get(`/policies/${id}/view`, {
      responseType: 'arraybuffer',
    });
    return response.data as ArrayBuffer;
  },
};
